import { Title } from '@solidjs/meta'
import { type Component, createMemo, createSignal, Show } from 'solid-js'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Chip from '~/components/Chip'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useServerTable } from '~/hooks/useServerTable'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { type Path, useNavigate } from '~/router'
import { type CompanyType, companyService } from '~/services/company.service'
import { resolveError } from '~/utils/error'
import { generateSlug } from '~/utils/slug'

const CompaniesPage: Component = () => {
  const alert = useAlert()
  const toast = useToast()
  const navigate = useNavigate()

  // Búsqueda y filtros server-side
  const [filtersOpen, setFiltersOpen] = createSignal(false)
  const [filterStatus, setFilterStatus] = createSignal('')
  const [filterSearch, debouncedSearch, setFilterSearch] = useDebouncedSignal('')

  const table = useServerTable<CompanyType>({
    fetcher: params => companyService.list(params as Parameters<typeof companyService.list>[0]),
    params: () => {
      const q = debouncedSearch().trim()
      const p: Record<string, string | number | undefined> = {}
      if (q) p.name = `%${q}%`
      if (filterStatus()) p.status = filterStatus()
      return p
    }
  })

  const activeFilters = createMemo(() => [filterStatus()].filter(v => v !== '').length + (filterSearch() ? 1 : 0))

  const clearFilters = () => {
    setFilterStatus('')
    setFilterSearch('')
  }

  const companyChips = createMemo(() => [
    ...(filterSearch() ? [{ label: `«${filterSearch()}»`, color: 'info' as const, icon: 'material-symbols-light:search', onRemove: () => setFilterSearch('') }] : []),
    ...(filterStatus() ? [{ label: filterStatus() === 'active' ? 'Activa' : 'Inactiva', color: 'primary' as const, onRemove: () => setFilterStatus('') }] : [])
  ])

  const [showModal, setShowModal] = createSignal(false)
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [nameValue, setNameValue] = createSignal('')
  const [slugValue, setSlugValue] = createSignal('')

  const openCreate = () => {
    setNameValue('')
    setSlugValue('')
    setShowModal(true)
  }

  /** La edición completa de la empresa (datos + preferencias) vive en su página. */
  const openEdit = (item: CompanyType) => {
    navigate(`/admin/empresas/${item.id}` as Path)
  }

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: formData.get('name') as string,
        slug: (formData.get('slug') as string) || (formData.get('name') as string).toLowerCase().replace(/\s+/g, '-'),
        domain: (formData.get('domain') as string) || undefined
      }
      await companyService.create(payload)
      toast.addToast({ title: 'Empresa creada', type: 'success' })
      setShowModal(false)
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar empresa',
      message: '¿Estás seguro de eliminar esta empresa?'
    })
    if (!ok) return
    try {
      await companyService.delete(id)
      toast.addToast({ title: 'Empresa eliminada', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    { header: 'Nombre', accessor: (c: CompanyType) => c.name },
    { header: 'Slug', accessor: (c: CompanyType) => c.slug },
    { header: 'Dominio', accessor: (c: CompanyType) => c.domain ?? '-' },
    {
      header: 'Estado',
      accessor: (c: CompanyType) =>
        c.status === 'active' ? (
          <Chip color="success" variant="soft" size="xs">
            Activo
          </Chip>
        ) : (
          <Chip color="default" variant="soft" size="xs">
            Inactivo
          </Chip>
        )
    }
  ]

  return (
    <>
      <Title>Empresas | Florez Jaramillo</Title>
      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>
      <Table<CompanyType>
        title="Empresas"
        subtitle="Gestión de empresas multi-tenant"
        headSticky
        headerActions={
          <div class="flex items-center gap-2">
            <FiltersButton activeCount={activeFilters()} onClick={() => setFiltersOpen(true)} />
            <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
              Nueva Empresa
            </Button>
          </div>
        }
        data={table.data()}
        columns={columns}
        keyField="id"
        loading={table.loading()}
        serverSide
        total={table.total()}
        page={table.page()}
        perPage={table.perPage()}
        onPageChange={table.onPageChange}
        onPerPageChange={table.onPerPageChange}
        emptyMessage="Aún no hay empresas"
        actions={item => (
          <ButtonGroup size="sm" variant="soft">
            <Button color="warning" onClick={() => openEdit(item)} tooltip="Configurar empresa" icon="material-symbols-light:settings-outline" />
            <Button color="danger" onClick={() => handleDelete(item.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
          </ButtonGroup>
        )}
      />

      <TableFilters title="Filtros de empresas" isOpen={filtersOpen()} onClose={() => setFiltersOpen(false)} activeCount={activeFilters()} onClear={clearFilters} chips={companyChips()}>
        <Input label="Buscar" value={filterSearch()} onInput={e => setFilterSearch(e.currentTarget.value)} placeholder="Nombre, slug o dominio..." startIcon="material-symbols-light:search" />

        <Select
          label="Estado"
          options={[
            { value: '', title: 'Cualquiera' },
            { value: 'active', title: 'Activa' },
            { value: 'inactive', title: 'Inactiva' }
          ]}
          value={filterStatus()}
          onChange={v => setFilterStatus(String(v ?? ''))}
          class="w-full"
        />
      </TableFilters>

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">Nueva Empresa</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="company-form" onSubmit={handleSubmit}>
            <div class="space-y-4">
              <Input name="name" label="Nombre" required value={nameValue()} onInput={e => setNameValue(e.currentTarget.value)} />
              <Input
                name="slug"
                label="Slug"
                placeholder="mi-empresa"
                value={slugValue()}
                onInput={e => setSlugValue(e.currentTarget.value)}
                endIcon={<Button size="sm" variant="text" icon="material-symbols-light:autorenew" onClick={() => setSlugValue(generateSlug(nameValue()))} title="Generar slug desde el nombre" />}
              />
              <Input name="domain" label="Dominio" placeholder="ejemplo.com" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting()} form="company-form">
              {isSubmitting() ? 'Guardando...' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default CompaniesPage
